import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { toast } from "sonner";
import { api, type MeUser } from "@/lib/api";

export type AuthUser = MeUser;

/**
 * useAuth — single source of truth for "am I logged in ?" across the app.
 *
 * Reads /api/me (proxied through Better Auth). Cached 60s. Never throws —
 * a 401 / missing token yields `{ user: null }`.
 *
 * `isLoading` is true only during the initial fetch ; subsequent navigations
 * use the cache so guards don't flash a "connect" screen on every page change.
 */
export function useAuth() {
  const q = useQuery({
    queryKey: ["me"],
    queryFn: api.me,
    retry: false,
    staleTime: 60_000,
  });
  return {
    user: (q.data?.user ?? null) as AuthUser | null,
    isAuthenticated: !!q.data?.user,
    isLoading: q.isLoading,
  };
}

/**
 * useRequireAuth — returns a `gate(action, label?)` function. If the user is
 * logged in, runs `action()`. If not, shows a French toast asking them to
 * sign in and offers a link to /login. Use it to wrap any mutation that hits
 * an auth-gated endpoint (follows, alerts, ...).
 */
export function useRequireAuth() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  return useCallback(
    (action: () => void, opts?: { actionLabel?: string }) => {
      if (isLoading) return; // wait until we know
      if (isAuthenticated) {
        action();
        return;
      }
      const label = opts?.actionLabel ?? "effectuer cette action";
      toast.error(`Vous devez être connecté pour ${label}.`, {
        action: {
          label: "Se connecter",
          onClick: () => navigate("/login"),
        },
        duration: 6000,
      });
    },
    [isAuthenticated, isLoading, navigate]
  );
}
